/**
 * Toast Notification Store
 *
 * Provides global toast notifications matching PHP's /js/toast.js functionality.
 *
 * PHP REFERENCE:
 * - showToast(message, type) function in /js/toast.js
 * - Used throughout admin for success/error feedback
 * - Types: 'success', 'error', 'warning', 'info'
 *
 * USAGE:
 * const toastStore = useToastStore();
 * toastStore.success('Course assigned successfully');
 * toastStore.error('Failed to save changes');
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration: number;
}

let toastId = 0;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([]);

  function addToast(message: string, type: Toast['type'] = 'info', duration = 5000) {
    const id = ++toastId;
    toasts.value.push({ id, message, type, duration });

    // Auto-remove after duration
    if (duration > 0) {
      timers.set(id, setTimeout(() => {
        removeToast(id);
      }, duration));
    }

    return id;
  }

  function removeToast(id: number) {
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }
    const index = toasts.value.findIndex((t) => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  }

  function pauseToast(id: number) {
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }
  }

  function resumeToast(id: number) {
    const toast = toasts.value.find((t) => t.id === id);
    if (toast && toast.duration > 0 && !timers.has(id)) {
      timers.set(id, setTimeout(() => {
        removeToast(id);
      }, toast.duration));
    }
  }

  function success(message: string, duration = 5000) {
    return addToast(message, 'success', duration);
  }

  function error(message: string, duration = 7000) {
    return addToast(message, 'error', duration);
  }

  function warning(message: string, duration = 6000) {
    return addToast(message, 'warning', duration);
  }

  function info(message: string, duration = 5000) {
    return addToast(message, 'info', duration);
  }

  function clear() {
    toasts.value = [];
  }

  return {
    toasts,
    addToast,
    removeToast,
    pauseToast,
    resumeToast,
    success,
    error,
    warning,
    info,
    clear,
  };
});
